import { AuthenticationAssuranceLevel, ScopeType, AuditPolicy, IdempotencyPolicy, ConcurrencyPolicy, PrivacyClassification, ActorType, } from './operation.js';
import * as OID from './operation-ids.js';
const BASE_READ = {
    requiredAal: AuthenticationAssuranceLevel.AAL1,
    idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
    concurrencyPolicy: ConcurrencyPolicy.NONE,
    auditPolicy: AuditPolicy.READ,
    privacyClassification: PrivacyClassification.INTERNAL,
    httpMethods: ['GET'],
    isReadOnly: true,
    isFinancial: false,
};
const BASE_WRITE = {
    requiredAal: AuthenticationAssuranceLevel.AAL2,
    idempotencyPolicy: IdempotencyPolicy.REQUIRED,
    concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
    auditPolicy: AuditPolicy.SENSITIVE,
    privacyClassification: PrivacyClassification.RESTRICTED,
    isReadOnly: false,
    isFinancial: false,
};
export const ACCESS_OPERATIONS = {
    [OID.PLATFORM_ORGANIZATIONS_LIST]: {
        id: OID.PLATFORM_ORGANIZATIONS_LIST,
        name: 'List Platform Organizations',
        labelKey: 'operations.platform.organizations.list.label',
        permission: 'platform:organizations:list',
        permissionId: 'platform:organizations:list',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_ORGANIZATIONS_LIST,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        requiredModules: ['PLATFORM', 'ADMIN'],
        requiredEntitlements: [],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'READ_ONLY',
        clientForcing: 'NONE',
        supportDelegationEligible: true,
        ...BASE_READ,
    },
    [OID.PLATFORM_ORGANIZATIONS_READ]: {
        id: OID.PLATFORM_ORGANIZATIONS_READ,
        name: 'Read Platform Organization',
        labelKey: 'operations.platform.organizations.read.label',
        permission: 'platform:organizations:read',
        permissionId: 'platform:organizations:read',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_ORGANIZATIONS_READ,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'READ_ONLY',
        clientForcing: 'NONE',
        supportDelegationEligible: true,
        ...BASE_READ,
    },
    [OID.PLATFORM_ORGANIZATIONS_CREATE]: {
        id: OID.PLATFORM_ORGANIZATIONS_CREATE,
        name: 'Create Platform Organization',
        labelKey: 'operations.platform.organizations.create.label',
        permission: 'platform:organizations:create',
        permissionId: 'platform:organizations:create',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_ORGANIZATIONS_CREATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.PLATFORM_ORGANIZATIONS_UPDATE]: {
        id: OID.PLATFORM_ORGANIZATIONS_UPDATE,
        name: 'Update Platform Organization',
        labelKey: 'operations.platform.organizations.update.label',
        permission: 'platform:organizations:update',
        permissionId: 'platform:organizations:update',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_ORGANIZATIONS_UPDATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.PLATFORM_ORGANIZATIONS_DISABLE]: {
        id: OID.PLATFORM_ORGANIZATIONS_DISABLE,
        name: 'Disable Platform Organization',
        labelKey: 'operations.platform.organizations.disable.label',
        permission: 'platform:organizations:disable',
        permissionId: 'platform:organizations:disable',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_ORGANIZATIONS_DISABLE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.PLATFORM_ORGANIZATIONS_OFFBOARDING_EXPORT]: {
        id: OID.PLATFORM_ORGANIZATIONS_OFFBOARDING_EXPORT,
        name: 'Export Organization Offboarding Bundle',
        labelKey: 'operations.platform.organizations.offboardingExport.label',
        permission: 'platform:organizations:offboarding-export',
        permissionId: 'platform:organizations:offboarding-export',
        module: 'PLATFORM',
        scopeTypes: [ScopeType.SYSTEM, ScopeType.PLATFORM],
        allowedScopeKinds: ['SYSTEM', 'PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_ORGANIZATIONS_OFFBOARDING_EXPORT,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.PLATFORM_ORGANIZATIONS_ONBOARDING_READINESS_READ]: {
        id: OID.PLATFORM_ORGANIZATIONS_ONBOARDING_READINESS_READ,
        name: 'Read Organization Onboarding Readiness',
        labelKey: 'operations.platform.organizations.onboardingReadiness.label',
        permission: 'platform:organizations:read',
        permissionId: 'platform:organizations:onboarding-readiness',
        module: 'PLATFORM',
        scopeTypes: [ScopeType.SYSTEM, ScopeType.PLATFORM],
        allowedScopeKinds: ['SYSTEM', 'PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_ORGANIZATIONS_ONBOARDING_READINESS_READ,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'READ',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_READ,
        httpMethods: ['GET'],
    },
    [OID.PLATFORM_ORGANIZATIONS_SUPPORT_COMMAND_ISSUE]: {
        id: OID.PLATFORM_ORGANIZATIONS_SUPPORT_COMMAND_ISSUE,
        name: 'Issue Support Command to Organization',
        labelKey: 'operations.platform.organizations.supportCommand.issue.label',
        permission: 'platform:organizations:support-command:issue',
        permissionId: 'platform:organizations:support-command:issue',
        module: 'PLATFORM',
        scopeTypes: [ScopeType.SYSTEM, ScopeType.PLATFORM],
        allowedScopeKinds: ['SYSTEM', 'PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_ORGANIZATIONS_SUPPORT_COMMAND_ISSUE,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
        httpMethods: ['POST'],
    },
    [OID.PLATFORM_MODULES_LIST]: {
        id: OID.PLATFORM_MODULES_LIST,
        name: 'List Platform Modules',
        labelKey: 'operations.platform.modules.list.label',
        permission: 'platform:modules:list',
        permissionId: 'platform:modules:list',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.SYSTEM, ScopeType.PLATFORM],
        allowedScopeKinds: ['SYSTEM', 'PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_MODULES_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'READ_ONLY',
        clientForcing: 'NONE',
        supportDelegationEligible: true,
        ...BASE_READ,
    },
    [OID.PLATFORM_MODULES_READ]: {
        id: OID.PLATFORM_MODULES_READ,
        name: 'Read Platform Module',
        labelKey: 'operations.platform.modules.read.label',
        permission: 'platform:modules:read',
        permissionId: 'platform:modules:read',
        module: 'PLATFORM',
        scopeTypes: [ScopeType.SYSTEM, ScopeType.PLATFORM],
        allowedScopeKinds: ['SYSTEM', 'PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_MODULES_READ,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'READ_ONLY',
        clientForcing: 'NONE',
        supportDelegationEligible: true,
        ...BASE_READ,
    },
    [OID.PLATFORM_MODULES_ASSIGN]: {
        id: OID.PLATFORM_MODULES_ASSIGN,
        name: 'Assign Platform Module',
        labelKey: 'operations.platform.modules.assign.label',
        permission: 'platform:modules:assign',
        permissionId: 'platform:modules:assign',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.SYSTEM, ScopeType.PLATFORM],
        allowedScopeKinds: ['SYSTEM', 'PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_MODULES_ASSIGN,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.PLATFORM_MODULES_REVOKE]: {
        id: OID.PLATFORM_MODULES_REVOKE,
        name: 'Revoke Platform Module',
        labelKey: 'operations.platform.modules.revoke.label',
        permission: 'platform:modules:revoke',
        permissionId: 'platform:modules:revoke',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.SYSTEM, ScopeType.PLATFORM],
        allowedScopeKinds: ['SYSTEM', 'PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_MODULES_REVOKE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_BRANCHES_LIST]: {
        id: OID.ORGANIZATION_BRANCHES_LIST,
        name: 'List Organization Branchs',
        labelKey: 'operations.organization.branches.list.label',
        permission: 'organization:branches:list',
        permissionId: 'organization:branches:list',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_BRANCHES_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.ORGANIZATION_BRANCHES_READ]: {
        id: OID.ORGANIZATION_BRANCHES_READ,
        name: 'Read Organization Branch',
        labelKey: 'operations.organization.branches.read.label',
        permission: 'organization:branches:read',
        permissionId: 'organization:branches:read',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_BRANCHES_READ,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.ORGANIZATION_BRANCHES_CREATE]: {
        id: OID.ORGANIZATION_BRANCHES_CREATE,
        name: 'Create Organization Branch',
        labelKey: 'operations.organization.branches.create.label',
        permission: 'organization:branches:create',
        permissionId: 'organization:branches:create',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_BRANCHES_CREATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_BRANCHES_UPDATE]: {
        id: OID.ORGANIZATION_BRANCHES_UPDATE,
        name: 'Update Organization Branch',
        labelKey: 'operations.organization.branches.update.label',
        permission: 'organization:branches:update',
        permissionId: 'organization:branches:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_BRANCHES_UPDATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_BRANCHES_DISABLE]: {
        id: OID.ORGANIZATION_BRANCHES_DISABLE,
        name: 'Disable Organization Branch',
        labelKey: 'operations.organization.branches.disable.label',
        permission: 'organization:branches:disable',
        permissionId: 'organization:branches:disable',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_BRANCHES_DISABLE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['DELETE'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_BRANCHES_MAKE_DEFAULT]: {
        id: OID.ORGANIZATION_BRANCHES_MAKE_DEFAULT,
        name: 'Make Default Organization Branch',
        labelKey: 'operations.organization.branches.make-default.label',
        permission: 'organization:branches:update',
        permissionId: 'organization:branches:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_BRANCHES_MAKE_DEFAULT,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENTS_LIST]: {
        id: OID.ORGANIZATION_CLIENTS_LIST,
        name: 'List Organization Clients',
        labelKey: 'operations.organization.clients.list.label',
        permission: 'organization:clients:list',
        permissionId: 'organization:clients:list',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENTS_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.ORGANIZATION_CLIENTS_READ]: {
        id: OID.ORGANIZATION_CLIENTS_READ,
        name: 'Read Organization Client',
        labelKey: 'operations.organization.clients.read.label',
        permission: 'organization:clients:read',
        permissionId: 'organization:clients:read',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENTS_READ,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            clientAccountId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_READ,
    },
    [OID.ORGANIZATION_CLIENTS_CREATE]: {
        id: OID.ORGANIZATION_CLIENTS_CREATE,
        name: 'Create Organization Client',
        labelKey: 'operations.organization.clients.create.label',
        permission: 'organization:clients:create',
        permissionId: 'organization:clients:create',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENTS_CREATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENTS_UPDATE]: {
        id: OID.ORGANIZATION_CLIENTS_UPDATE,
        name: 'Update Organization Client',
        labelKey: 'operations.organization.clients.update.label',
        permission: 'organization:clients:update',
        permissionId: 'organization:clients:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENTS_UPDATE,
        allowedActorTypes: [ActorType.USER],
        // Suitability assessment, independent approval, and material-change review
        // are lifecycle writes under this client-update authority.
        httpMethods: ['PATCH', 'POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            clientAccountId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENTS_DISABLE]: {
        id: OID.ORGANIZATION_CLIENTS_DISABLE,
        name: 'Disable Organization Client',
        labelKey: 'operations.organization.clients.disable.label',
        permission: 'organization:clients:disable',
        permissionId: 'organization:clients:disable',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENTS_DISABLE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            clientAccountId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNTS_LIST]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
        name: 'List Organization Client Accounts',
        labelKey: 'operations.organization.clientAccounts.list.label',
        permission: 'organization:client-accounts:list',
        permissionId: 'organization:client-accounts:list',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNTS_READ]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNTS_READ,
        name: 'Read Organization Client Account',
        labelKey: 'operations.organization.clientAccounts.read.label',
        permission: 'organization:client-accounts:read',
        permissionId: 'organization:client-accounts:read',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNTS_READ,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            clientAccountId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_READ,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNTS_CREATE]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNTS_CREATE,
        name: 'Create Organization Client Account',
        labelKey: 'operations.organization.clientAccounts.create.label',
        permission: 'organization:client-accounts:create',
        permissionId: 'organization:client-accounts:create',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNTS_CREATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNTS_UPDATE]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNTS_UPDATE,
        name: 'Update Organization Client Account',
        labelKey: 'operations.organization.clientAccounts.update.label',
        permission: 'organization:client-accounts:update',
        permissionId: 'organization:client-accounts:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNTS_UPDATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            clientAccountId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNTS_DISABLE]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNTS_DISABLE,
        name: 'Disable Organization Client Account',
        labelKey: 'operations.organization.clientAccounts.disable.label',
        permission: 'organization:client-accounts:disable',
        permissionId: 'organization:client-accounts:disable',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNTS_DISABLE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            clientAccountId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_LIST]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_LIST,
        name: 'List Organization Client Account Documents',
        labelKey: 'operations.organization.clientAccounts.documents.list.label',
        permission: 'organization:client-accounts:read',
        permissionId: 'organization:client-accounts:read',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: { clientAccountId: [{ source: 'path', field: 'id' }] },
        ...BASE_READ,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_CREATE]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_CREATE,
        name: 'Register Organization Client Account Document',
        labelKey: 'operations.organization.clientAccounts.documents.create.label',
        permission: 'organization:client-accounts:update',
        permissionId: 'organization:client-accounts:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_CREATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: { clientAccountId: [{ source: 'path', field: 'id' }] },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_REMOVE]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_REMOVE,
        name: 'Remove Organization Client Account Document',
        labelKey: 'operations.organization.clientAccounts.documents.remove.label',
        permission: 'organization:client-accounts:update',
        permissionId: 'organization:client-accounts:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_REMOVE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['DELETE'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: { clientAccountId: [{ source: 'path', field: 'id' }] },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_LIST]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_LIST,
        name: 'List Organization Client Account Signatories',
        labelKey: 'operations.organization.clientAccounts.signatories.list.label',
        permission: 'organization:client-accounts:read',
        permissionId: 'organization:client-accounts:read',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: { clientAccountId: [{ source: 'path', field: 'id' }] },
        ...BASE_READ,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_CREATE]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_CREATE,
        name: 'Create Organization Client Account Signatory',
        labelKey: 'operations.organization.clientAccounts.signatories.create.label',
        permission: 'organization:client-accounts:update',
        permissionId: 'organization:client-accounts:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_CREATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: { clientAccountId: [{ source: 'path', field: 'id' }] },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_REMOVE]: {
        id: OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_REMOVE,
        name: 'Remove Organization Client Account Signatory',
        labelKey: 'operations.organization.clientAccounts.signatories.remove.label',
        permission: 'organization:client-accounts:update',
        permissionId: 'organization:client-accounts:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_REMOVE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['DELETE'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: { clientAccountId: [{ source: 'path', field: 'id' }] },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_PORTFOLIOS_LIST]: {
        id: OID.ORGANIZATION_PORTFOLIOS_LIST,
        name: 'List Organization Portfolios',
        labelKey: 'operations.organization.portfolios.list.label',
        permission: 'organization:portfolios:list',
        permissionId: 'organization:portfolios:list',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT', 'PORTFOLIO'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_PORTFOLIOS_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            clientAccountId: [{ source: 'query', field: 'party' }],
        },
        ...BASE_READ,
    },
    [OID.ORGANIZATION_PORTFOLIOS_READ]: {
        id: OID.ORGANIZATION_PORTFOLIOS_READ,
        name: 'Read Organization Portfolio',
        labelKey: 'operations.organization.portfolios.read.label',
        permission: 'organization:portfolios:read',
        permissionId: 'organization:portfolios:read',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.PORTFOLIO],
        allowedScopeKinds: ['ORGANIZATION', 'PORTFOLIO'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_PORTFOLIOS_READ,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_READ,
    },
    [OID.ORGANIZATION_PORTFOLIOS_CREATE]: {
        id: OID.ORGANIZATION_PORTFOLIOS_CREATE,
        name: 'Create Organization Portfolio',
        labelKey: 'operations.organization.portfolios.create.label',
        permission: 'organization:portfolios:create',
        permissionId: 'organization:portfolios:create',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.CLIENT],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION', 'CLIENT'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_PORTFOLIOS_CREATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_PORTFOLIOS_UPDATE]: {
        id: OID.ORGANIZATION_PORTFOLIOS_UPDATE,
        name: 'Update Organization Portfolio',
        labelKey: 'operations.organization.portfolios.update.label',
        permission: 'organization:portfolios:update',
        permissionId: 'organization:portfolios:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.PORTFOLIO],
        allowedScopeKinds: ['ORGANIZATION', 'PORTFOLIO'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_PORTFOLIOS_UPDATE,
        allowedActorTypes: [ActorType.USER],
        // Portfolio lifecycle updates use PATCH, while governed mandate decisions
        // are explicit POST commands on the same organization-portfolio update
        // authority. Keep the canonical method contract aligned with both routes.
        httpMethods: ['PATCH', 'POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_PORTFOLIOS_DISABLE]: {
        id: OID.ORGANIZATION_PORTFOLIOS_DISABLE,
        name: 'Disable Organization Portfolio',
        labelKey: 'operations.organization.portfolios.disable.label',
        permission: 'organization:portfolios:disable',
        permissionId: 'organization:portfolios:disable',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.PORTFOLIO],
        allowedScopeKinds: ['ORGANIZATION', 'PORTFOLIO'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_PORTFOLIOS_DISABLE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_LIST]: {
        id: OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_LIST, name: 'List Benchmark Assignments', labelKey: 'operations.organization.portfolios.read.label', permission: 'organization:portfolios:read', permissionId: 'organization:portfolios:read', module: 'ADMIN', scopeTypes: [ScopeType.ORGANIZATION, ScopeType.PORTFOLIO], allowedScopeKinds: ['ORGANIZATION', 'PORTFOLIO'], forcesClientScope: false, makerCheckerRequired: false, telemetryId: OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_LIST, allowedActorTypes: [ActorType.USER], requiredModules: ['ADMIN'], accessType: 'ADMIN', ownershipPolicy: 'ACTIVE_ORGANIZATION', delegationPolicy: 'NOT_ALLOWED', clientForcing: 'NONE', allowedPersonas: ['ORG_ADMIN'], supportDelegationEligible: false, requestedScope: { portfolioId: [{ source: 'path', field: 'id' }] }, ...BASE_READ,
    },
    [OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_CREATE]: {
        id: OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_CREATE, name: 'Create Benchmark Assignment', labelKey: 'operations.organization.portfolios.create.label', permission: 'organization:portfolios:update', permissionId: 'organization:portfolios:update', module: 'ADMIN', scopeTypes: [ScopeType.ORGANIZATION, ScopeType.PORTFOLIO], allowedScopeKinds: ['ORGANIZATION', 'PORTFOLIO'], forcesClientScope: false, makerCheckerRequired: true, telemetryId: OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_CREATE, allowedActorTypes: [ActorType.USER], httpMethods: ['POST'], requiredModules: ['ADMIN'], accessType: 'ADMIN', ownershipPolicy: 'ACTIVE_ORGANIZATION', delegationPolicy: 'NOT_ALLOWED', clientForcing: 'NONE', allowedPersonas: ['ORG_ADMIN'], supportDelegationEligible: false, requestedScope: { portfolioId: [{ source: 'path', field: 'id' }] }, ...BASE_WRITE,
    },
    [OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_APPROVE]: {
        id: OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_APPROVE, name: 'Approve Benchmark Assignment', labelKey: 'operations.organization.portfolios.update.label', permission: 'organization:portfolios:update', permissionId: 'organization:portfolios:update', module: 'ADMIN', scopeTypes: [ScopeType.ORGANIZATION, ScopeType.PORTFOLIO], allowedScopeKinds: ['ORGANIZATION', 'PORTFOLIO'], forcesClientScope: false, makerCheckerRequired: true, telemetryId: OID.ORGANIZATION_BENCHMARK_ASSIGNMENTS_APPROVE, allowedActorTypes: [ActorType.USER], httpMethods: ['POST'], requiredModules: ['ADMIN'], accessType: 'ADMIN', ownershipPolicy: 'ACTIVE_ORGANIZATION', delegationPolicy: 'NOT_ALLOWED', clientForcing: 'NONE', allowedPersonas: ['ORG_ADMIN'], supportDelegationEligible: false, requestedScope: { portfolioId: [{ source: 'path', field: 'id' }] }, ...BASE_WRITE,
    },
    [OID.PLATFORM_MODULES_CATALOG_LIST]: {
        id: OID.PLATFORM_MODULES_CATALOG_LIST,
        name: 'List Platform Module Catalog',
        labelKey: 'operations.platform.modules.catalog.list.label',
        permission: 'platform:modules:list',
        permissionId: 'platform:modules:list',
        module: 'PLATFORM',
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_MODULES_CATALOG_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['PLATFORM'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'READ_ONLY',
        clientForcing: 'NONE',
        supportDelegationEligible: true,
        ...BASE_READ,
    },
    [OID.PLATFORM_MODULES_CATALOG_MANAGE]: {
        id: OID.PLATFORM_MODULES_CATALOG_MANAGE,
        name: 'Manage Platform Module Catalog',
        labelKey: 'operations.platform.modules.catalog.manage.label',
        permission: 'platform:modules:assign',
        permissionId: 'platform:modules:assign',
        module: 'PLATFORM',
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_MODULES_CATALOG_MANAGE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['PLATFORM'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.PLATFORM_MODULES_ASSIGN_TO_ORGANIZATION]: {
        id: OID.PLATFORM_MODULES_ASSIGN_TO_ORGANIZATION,
        name: 'Assign Module To Organization',
        labelKey: 'operations.platform.modules.assignToOrganization.label',
        permission: 'platform:modules:assign',
        permissionId: 'platform:modules:assign',
        module: 'PLATFORM',
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_MODULES_ASSIGN_TO_ORGANIZATION,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['PLATFORM'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.PLATFORM_MODULES_REVOKE_FROM_ORGANIZATION]: {
        id: OID.PLATFORM_MODULES_REVOKE_FROM_ORGANIZATION,
        name: 'Revoke Module From Organization',
        labelKey: 'operations.platform.modules.revokeFromOrganization.label',
        permission: 'platform:modules:revoke',
        permissionId: 'platform:modules:revoke',
        module: 'PLATFORM',
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_MODULES_REVOKE_FROM_ORGANIZATION,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['PLATFORM'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'EXPLICITLY_GRANTED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_MODULES_LIST_ASSIGNED]: {
        id: OID.ORGANIZATION_MODULES_LIST_ASSIGNED,
        name: 'List Assigned Organization Modules',
        labelKey: 'operations.organization.modules.listAssigned.label',
        permission: 'organization:modules:list',
        permissionId: 'organization:modules:list',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_MODULES_LIST_ASSIGNED,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.ORGANIZATION_MODULES_ENABLE]: {
        id: OID.ORGANIZATION_MODULES_ENABLE,
        name: 'Enable Organization Module',
        labelKey: 'operations.organization.modules.enable.label',
        permission: 'organization:modules:enable',
        permissionId: 'organization:modules:enable',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_MODULES_ENABLE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_MODULES_DISABLE]: {
        id: OID.ORGANIZATION_MODULES_DISABLE,
        name: 'Disable Organization Module',
        labelKey: 'operations.organization.modules.disable.label',
        permission: 'organization:modules:disable',
        permissionId: 'organization:modules:disable',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_MODULES_DISABLE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.ORGANIZATION_MODULES_CONFIGURE]: {
        id: OID.ORGANIZATION_MODULES_CONFIGURE,
        name: 'Configure Organization Module',
        labelKey: 'operations.organization.modules.configure.label',
        permission: 'organization:modules:configure',
        permissionId: 'organization:modules:configure',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_MODULES_CONFIGURE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.CLIENT_DASHBOARD_READ]: {
        id: OID.CLIENT_DASHBOARD_READ,
        name: 'Read Client Dashboard',
        labelKey: 'operations.client.dashboard.read.label',
        permission: 'client:dashboard:read',
        permissionId: 'client:dashboard:read',
        module: 'DASHBOARD',
        scopeTypes: [ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        allowedScopeKinds: ['CLIENT', 'PORTFOLIO'],
        forcesClientScope: true,
        makerCheckerRequired: false,
        telemetryId: OID.CLIENT_DASHBOARD_READ,
        allowedActorTypes: [ActorType.CLIENT],
        requiredModules: ['DASHBOARD'],
        accessType: 'READ',
        ownershipPolicy: 'OWN_CLIENT',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'REQUIRED',
        allowedPersonas: ['ORG_CLIENT'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.CLIENT_PROFILE_READ]: {
        id: OID.CLIENT_PROFILE_READ,
        name: 'Read Own Client Profile',
        labelKey: 'operations.client.profile.read.label',
        permission: 'client:profile:read',
        permissionId: 'client:profile:read',
        module: 'SETTINGS',
        requiresEntitledModule: false,
        scopeTypes: [ScopeType.CLIENT],
        requestedScope: {
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
        },
        allowedScopeKinds: ['CLIENT'],
        forcesClientScope: true,
        makerCheckerRequired: false,
        telemetryId: OID.CLIENT_PROFILE_READ,
        allowedActorTypes: [ActorType.CLIENT],
        requiredModules: ['SETTINGS'],
        accessType: 'READ',
        ownershipPolicy: 'OWN_CLIENT',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'REQUIRED',
        allowedPersonas: ['ORG_CLIENT'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.CLIENT_PROFILE_UPDATE_LIMITED]: {
        id: OID.CLIENT_PROFILE_UPDATE_LIMITED,
        name: 'Update Own Client Profile (Limited)',
        labelKey: 'operations.client.profile.updateLimited.label',
        permission: 'client:profile:limited-update',
        permissionId: 'client:profile:limited-update',
        module: 'SETTINGS',
        requiresEntitledModule: false,
        scopeTypes: [ScopeType.CLIENT],
        requestedScope: {
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
        },
        allowedScopeKinds: ['CLIENT'],
        forcesClientScope: true,
        makerCheckerRequired: false,
        telemetryId: OID.CLIENT_PROFILE_UPDATE_LIMITED,
        allowedActorTypes: [ActorType.CLIENT],
        httpMethods: ['PATCH'],
        requiredModules: ['SETTINGS'],
        accessType: 'WRITE',
        ownershipPolicy: 'OWN_CLIENT',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'REQUIRED',
        allowedPersonas: ['ORG_CLIENT'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.CLIENT_HOLDINGS_READ_OWN]: {
        id: OID.CLIENT_HOLDINGS_READ_OWN,
        name: 'Read Own Holdings',
        labelKey: 'operations.client.holdings.readOwn.label',
        permission: 'client:holdings:read-own',
        permissionId: 'client:holdings:read-own',
        module: 'HOLDINGS',
        scopeTypes: [ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        allowedScopeKinds: ['CLIENT', 'PORTFOLIO'],
        forcesClientScope: true,
        makerCheckerRequired: false,
        telemetryId: OID.CLIENT_HOLDINGS_READ_OWN,
        allowedActorTypes: [ActorType.CLIENT],
        requiredModules: ['HOLDINGS'],
        accessType: 'READ',
        ownershipPolicy: 'OWN_CLIENT',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'REQUIRED',
        allowedPersonas: ['ORG_CLIENT'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.CLIENT_TRANSACTIONS_READ_OWN]: {
        id: OID.CLIENT_TRANSACTIONS_READ_OWN,
        name: 'Read Own Transactions',
        labelKey: 'operations.client.transactions.readOwn.label',
        permission: 'client:transactions:read-own',
        permissionId: 'client:transactions:read-own',
        module: 'INVESTMENT',
        scopeTypes: [ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        allowedScopeKinds: ['CLIENT', 'PORTFOLIO'],
        forcesClientScope: true,
        makerCheckerRequired: false,
        telemetryId: OID.CLIENT_TRANSACTIONS_READ_OWN,
        allowedActorTypes: [ActorType.CLIENT],
        requiredModules: ['INVESTMENT'],
        accessType: 'READ',
        ownershipPolicy: 'OWN_CLIENT',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'REQUIRED',
        allowedPersonas: ['ORG_CLIENT'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.CLIENT_REPORTS_READ_OWN]: {
        id: OID.CLIENT_REPORTS_READ_OWN,
        name: 'Read Own Reports',
        labelKey: 'operations.client.reports.readOwn.label',
        permission: 'client:reports:read-own',
        permissionId: 'client:reports:read-own',
        module: 'REPORTING',
        scopeTypes: [ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        allowedScopeKinds: ['CLIENT', 'PORTFOLIO'],
        forcesClientScope: true,
        makerCheckerRequired: false,
        telemetryId: OID.CLIENT_REPORTS_READ_OWN,
        allowedActorTypes: [ActorType.CLIENT],
        requiredModules: ['REPORTING'],
        accessType: 'READ',
        ownershipPolicy: 'OWN_CLIENT',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'REQUIRED',
        allowedPersonas: ['ORG_CLIENT'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.CLIENT_REPORTS_EXPORT_OWN]: {
        id: OID.CLIENT_REPORTS_EXPORT_OWN,
        name: 'Export Own Reports',
        labelKey: 'operations.client.reports.exportOwn.label',
        permission: 'client:reports:export-own',
        permissionId: 'client:reports:export-own',
        module: 'REPORTING',
        scopeTypes: [ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        allowedScopeKinds: ['CLIENT', 'PORTFOLIO'],
        forcesClientScope: true,
        makerCheckerRequired: false,
        telemetryId: OID.CLIENT_REPORTS_EXPORT_OWN,
        allowedActorTypes: [ActorType.CLIENT],
        httpMethods: ['POST'],
        requiredModules: ['REPORTING'],
        accessType: 'EXPORT',
        ownershipPolicy: 'OWN_CLIENT',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'REQUIRED',
        allowedPersonas: ['ORG_CLIENT'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
    [OID.CLIENT_DOCUMENTS_READ_OWN]: {
        id: OID.CLIENT_DOCUMENTS_READ_OWN,
        name: 'Read Own Documents',
        labelKey: 'operations.client.documents.readOwn.label',
        permission: 'client:documents:read-own',
        permissionId: 'client:documents:read-own',
        module: 'REPORTING',
        scopeTypes: [ScopeType.CLIENT],
        requestedScope: {
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
        },
        allowedScopeKinds: ['CLIENT'],
        forcesClientScope: true,
        makerCheckerRequired: false,
        telemetryId: OID.CLIENT_DOCUMENTS_READ_OWN,
        allowedActorTypes: [ActorType.CLIENT],
        requiredModules: ['REPORTING'],
        accessType: 'READ',
        ownershipPolicy: 'OWN_CLIENT',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'REQUIRED',
        allowedPersonas: ['ORG_CLIENT'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.PLATFORM_SUPPORT_DELEGATIONS_LIST]: {
        id: OID.PLATFORM_SUPPORT_DELEGATIONS_LIST,
        name: 'List Platform Support Delegations',
        labelKey: 'operations.platform.supportDelegations.list.label',
        permission: 'platform:support-delegations:list',
        permissionId: 'platform:support-delegations:list',
        module: 'PLATFORM',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.PLATFORM_SUPPORT_DELEGATIONS_LIST,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['PLATFORM', 'ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'READ_ONLY',
        clientForcing: 'NONE',
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.ORGANIZATION_PROFILE_READ]: {
        id: OID.ORGANIZATION_PROFILE_READ,
        name: 'Read Organization Profile',
        labelKey: 'operations.organization.profile.read.label',
        permission: 'organization:profile:read',
        permissionId: 'organization:profile:read',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_PROFILE_READ,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.ORGANIZATION_PROFILE_DETAIL_READ]: {
        id: OID.ORGANIZATION_PROFILE_DETAIL_READ,
        name: 'Read Organization Profile Detail',
        labelKey: 'operations.organization.profile.detail.read.label',
        descriptionKey: 'operations.organization.profile.detail.read.description',
        permission: 'organization:profile:read',
        permissionId: 'organization:profile:read',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_PROFILE_DETAIL_READ,
        allowedActorTypes: [ActorType.USER],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_READ,
    },
    [OID.ORGANIZATION_PROFILE_UPDATE]: {
        id: OID.ORGANIZATION_PROFILE_UPDATE,
        name: 'Update Organization Profile',
        labelKey: 'operations.organization.profile.update.label',
        permission: 'organization:profile:update',
        permissionId: 'organization:profile:update',
        module: 'ADMIN',
        scopeTypes: [ScopeType.ORGANIZATION],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
        },
        allowedScopeKinds: ['ORGANIZATION'],
        forcesClientScope: false,
        makerCheckerRequired: false,
        telemetryId: OID.ORGANIZATION_PROFILE_UPDATE,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        requiredModules: ['ADMIN'],
        accessType: 'ADMIN',
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        allowedPersonas: ['ORG_ADMIN'],
        supportDelegationEligible: false,
        ...BASE_WRITE,
    },
};
